import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';

import {
    buildToolStepCollectionKey,
    buildToolStepConstellationKey,
    type WorkflowKeyScope,
} from '@/workflow/_pure/keyCodec';
import { selectActivationTraceSnapshotView } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type {
    PostItemTraceContext,
    PreItemTraceContext,
    TraceSnapshotResolver,
} from '@/workflow-runner/_contract/pipelineContext';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';


export function derivePreItemTraceContext(args: {
    cosmosData: CosmosDataModel;
    runtimeState: RuntimeState;
    config: Config;
    traceSnapshotResolver: TraceSnapshotResolver;
    pendingMoveSnapshotRole?: 'from' | 'to';
}): PreItemTraceContext {
    const { cosmosData, runtimeState, traceSnapshotResolver } = args;
    const pendingReplayMove = runtimeState.pendingReplayMove;

    const traceSnapshot = pendingReplayMove
        ? traceSnapshotResolver.deriveTraceSnapshotAtPosition({
            cosmosData,
            runtimeState,
            replayPositionIndex: args.pendingMoveSnapshotRole === 'from'
                ? pendingReplayMove.fromPositionIndex
                : pendingReplayMove.toPositionIndex,
        })
        : traceSnapshotResolver.deriveTraceSnapshot({ cosmosData, runtimeState });
    const targetTraceSnapshotView = pendingReplayMove?.eventKind === 'primitive-operation'
        ? traceSnapshotResolver.deriveTraceSnapshotAtPosition({
            cosmosData,
            runtimeState,
            replayPositionIndex: pendingReplayMove.toPositionIndex,
        })
        : undefined;
    const activationTraceSnapshotView = selectActivationTraceSnapshotView({
        traceSnapshotView: traceSnapshot,
        targetTraceSnapshotView,
        pendingReplayEventKind: pendingReplayMove?.eventKind,
    });

    const workflowHandle = traceSnapshot?.mutableWorkflowTrace.workflowHandle;

    const workflowKeyScope = workflowHandle === undefined
        ? undefined
        : { workflowHandle, threadIndex: 0 } satisfies WorkflowKeyScope;

    const toolStepCollectionKey = workflowKeyScope === undefined
        ? undefined
        : buildToolStepCollectionKey(workflowKeyScope);
        
    const toolStepConstellationKey = workflowKeyScope === undefined
        ? undefined
        : buildToolStepConstellationKey(workflowKeyScope);

    return {
        traceSnapshot,
        traceSnapshotView: traceSnapshot,
        activationTraceSnapshotView,
        workflowKeyScope,
        toolStepCollectionKey,
        toolStepConstellationKey,
    };
}

/**
 * Derives the shared trace context after item collections are available.
 * Called by post-item controller methods in modeAugmentation.ts (boundary layer).
 * Must not be called from the pure boundary.
 */
export function derivePostItemTraceContext(args: {
    cosmosData: CosmosDataModel;
    runtimeState: RuntimeState;
    config: Config;
    itemCollections: ItemCollection[];
    preItemTraceContext?: PreItemTraceContext;
    traceSnapshotResolver: TraceSnapshotResolver;
}): PostItemTraceContext {
    const { cosmosData, config } = args;
    const preItemTraceContext = args.preItemTraceContext ?? derivePreItemTraceContext({
        cosmosData,
        runtimeState: args.runtimeState,
        config,
        traceSnapshotResolver: args.traceSnapshotResolver,
    });

    return preItemTraceContext;
}
